import { type Row, type RowData } from '@tanstack/react-table';
import type { ScrollToOptions } from '@tanstack/react-virtual';
import {
  useRef,
  type PointerEvent,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useIntl } from 'react-intl';

import { useFocusRing } from '../../../../../core/hooks/useFocusRing.js';
import { mergeProps } from '../../../../../core/utils/merge-props.js';
import { isTargetInteractive } from '../../../../utils/is-target-interactive.js';
import {
  useDataTable,
  useDataTableContent,
} from '../../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../../messages.js';
import type { TDataTableFeatures } from '../../types/features.types.js';
import * as styles from '../RowInteractivity.sty.js';
/**
 * Provides all necessary props for an interactive row element.
 *
 * @param row - the table row element
 * @param scrollToIndex - rowVirtualizer function for scrolling to the row with the specified index
 * @returns className and props for the row element
 */
export function useRowInteractivityProps<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
  scrollToIndex: (index: number, options?: ScrollToOptions) => void,
): {
  rowInteractivityClassName: string;
  rowInteractivityProps: Record<string, unknown> | undefined;
} {
  const table = useDataTable();
  const intl = useIntl();
  const { isHorizontallyScrollable } = useDataTableContent();
  // used to detect if the focus event was triggered by a click
  const clicked = useRef(false);
  const { isFocusVisible, focusProps } = useFocusRing();

  if (!row.getCanActivateRow()) {
    return { rowInteractivityClassName: '', rowInteractivityProps: undefined };
  }

  const isActivated = row.getIsActivated();
  const isAutoActivationEnabled = table.options.enableAutoActivation;
  const hasPinnedColumns =
    table.getStartVisibleLeafColumns().length > 0 ||
    table.getEndVisibleLeafColumns().length > 0;
  const rowInteractivityClassName = styles.interactiveRow({
    hovered: table.getHoveredRowId() === row.id,
    active: isActivated,
    focus: isFocusVisible,
    contained: table.atoms.visualVariantConfig?.get().contained,
    // Two-tone shadow risks a corner-pixel mismatch only when a pinned edge can scroll out of view.
    singleTone: hasPinnedColumns && isHorizontallyScrollable,
  });

  function handleRowActivation(event: ReactMouseEvent<HTMLElement>): void {
    const { target } = event;

    if (
      table.options.enableRowInteractivity !== true ||
      (target instanceof HTMLElement === false &&
        target instanceof SVGElement === false) ||
      // prevent deactivation if the user clicks on a Gantt segment (SVG <rect>) and the row is already active
      (target.nodeName === 'rect' && isActivated)
    ) {
      return;
    }

    const rowElement = target.closest('[role="row"]');
    if (rowElement && !isTargetInteractive(rowElement, target)) {
      row.toggleActivated(event);
    }
  }

  function isSelectionInsideTable(): boolean {
    const selection = globalThis.getSelection();

    if (!selection?.toString()) {
      return false;
    }

    const container = table.getRowVirtualizationContainer();
    if (!container) {
      return false;
    }

    // Use anchorNode (where the selection started) to determine whether the
    // selection originated inside the table. A selection outside the table
    // should not prevent row activation.
    let anchorNode = selection.anchorNode;
    if (anchorNode?.nodeType === Node.TEXT_NODE) {
      anchorNode = anchorNode.parentElement;
    }

    return !!anchorNode && container.contains(anchorNode);
  }

  const rowInteractivityProps = {
    tabIndex: 0,
    'data-trigger': 'interactive-row',
    'aria-selected': isActivated,
    'aria-roledescription': intl.formatMessage(
      dataTableMessages.interactiveRowRoleDescription,
    ),
    'aria-label': intl.formatMessage(dataTableMessages.interactiveRowTrigger, {
      isActivated,
      rowId: row.id,
    }),
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      clicked.current = true;
    },
    onClick: (event: ReactMouseEvent<HTMLElement>) => {
      // prevent row action if the user is selecting text inside the table and ensure
      // that double click doesn't trigger the row action twice
      // (event.detail represents the click count, 1 indicates a single click)
      if (!isSelectionInsideTable() && event.detail === 1) {
        handleRowActivation(event);
      }
    },
    onAuxClick: (event: ReactMouseEvent<HTMLElement>) => {
      // only handle middle mouse button (button 1), ignore right-click and others
      if (event.button === 1) {
        handleRowActivation(event);
      }
    },
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
      // Cell and row actions menus are portaled out of the row, but React still
      // bubbles their events here. Without this guard, arrow keys navigating to
      // an open menu would move the row focus and close the menu.
      // Only keys pressed inside the row itself are relevant for row interactivity.
      if (
        !(event.target instanceof Node) ||
        !event.currentTarget.contains(event.target)
      ) {
        return;
      }

      const isRowDragging =
        (table.atoms.draggingRowInfo?.get() ?? null) !== null;
      if (isRowDragging) {
        return;
      }

      const { key, target } = event;

      if (
        key === 'Enter' &&
        !isAutoActivationEnabled &&
        (target instanceof HTMLElement || target instanceof SVGElement)
      ) {
        const rowElement = target.closest('[role="row"]');

        if (rowElement && !isTargetInteractive(rowElement, target)) {
          event.preventDefault();
          event.stopPropagation();
          row.toggleActivated(event);
          return;
        }
      }

      let offset: number | null = null;
      switch (key) {
        case 'ArrowDown': {
          offset = +1;
          break;
        }
        case 'ArrowUp': {
          offset = -1;
          break;
        }
        case 'PageUp': {
          offset = -10;
          break;
        }
        case 'PageDown': {
          offset = +10;
          break;
        }
        case 'Home': {
          offset = -Infinity;
          break;
        }
        case 'End': {
          offset = Infinity;
          break;
        }
      }

      if (offset === null) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      const allRows = table.getRowModel().rows;
      const currentRowIndex = allRows.findIndex(
        (currentRow) => currentRow.id === row.id,
      );
      if (currentRowIndex === -1) {
        return;
      }

      const clampedIndex = Math.max(
        0,
        Math.min(currentRowIndex + offset, allRows.length - 1),
      );
      const nextRow = allRows.at(clampedIndex);
      if (nextRow === undefined) {
        return;
      }

      scrollToIndex(clampedIndex, {
        align: 'auto',
      });
      requestAnimationFrame(() => {
        const nextRowButton = table
          .getRowVirtualizationContainer()
          ?.querySelector<HTMLButtonElement>(
            `[data-rowid="${CSS.escape(
              nextRow.id,
            )}"][data-trigger="interactive-row"]`,
          );
        nextRowButton?.focus();
      });
    },
    ...mergeProps(focusProps, {
      onFocus: (event: ReactFocusEvent<HTMLElement>) => {
        // only activate row if focus was triggered on the row itself
        if (event.currentTarget !== event.target) {
          return;
        }
        // When a row is initially activated, and you click on it to deactivate it using a pointing device,
        // the `pointerdown` event is called before the `focus` event. This code is needed in order to
        // not re-activate the row again. It also ensures that the focus ring is still correctly
        // moved within the table.
        if (clicked.current) {
          clicked.current = false;
          return;
        }

        if (!isActivated && isAutoActivationEnabled) {
          row.toggleActivated(event);
        }
      },
    }),
  };

  return { rowInteractivityClassName, rowInteractivityProps };
}
